import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface Holding {
  id: string;
  holding_name: string;
  holding_type: string;
  description: string;
  initial_value: number;
  current_value: number;
  acquisition_date: string;
  status: string;
  notes: string;
  performance_metrics: any;
  created_at: string;
}

export const useHoldings = (selectedAthleteId?: string) => {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, accountType } = useUser();

  const fetchHoldings = async () => {
    if (!currentUser?.id) return;

    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('holdings').select('*');

      if (accountType === 'agent' && selectedAthleteId && selectedAthleteId !== 'all' && selectedAthleteId !== 'my-holdings') {
        // For agents viewing specific talent's holdings
        const { data: talentData } = await supabase
          .from('talents')
          .select('profile_id')
          .eq('id', selectedAthleteId)
          .single();
        
        if (talentData?.profile_id) {
          query = query.eq('profile_id', talentData.profile_id);
        }
      } else if (accountType === 'agent' && selectedAthleteId === 'all') {
        // For agents viewing all linked talents' holdings
        const { data: agentProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (agentProfile) {
          const { data: agent } = await supabase
            .from('agents')
            .select('id')
            .eq('profile_id', agentProfile.id)
            .single();
          
          if (agent) {
            const { data: linkedTalents } = await supabase
              .from('talents')
              .select('profile_id')
              .eq('agent_id', agent.id);
            
            if (linkedTalents && linkedTalents.length > 0) {
              const profileIds = linkedTalents.map(a => a.profile_id);
              query = query.in('profile_id', profileIds);
            }
          }
        }
      } else {
        // Default: user's own holdings
        query = query.eq('profile_id', currentUser.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setHoldings(data || []);
    } catch (err) {
      console.error('Error fetching holdings:', err);
      setError('Failed to load holdings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHoldings();
  }, [currentUser?.id, selectedAthleteId]);

  const addHolding = async (holdingData: Omit<Holding, 'id' | 'created_at'>) => {
    if (!currentUser?.id) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('holdings')
      .insert({
        profile_id: currentUser.id,
        ...holdingData,
      })
      .select()
      .single();

    if (error) throw error;
    
    await fetchHoldings(); // Refresh the list
    return data;
  };

  const updateHolding = async (id: string, updates: Partial<Holding>) => {
    const { data, error } = await supabase
      .from('holdings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    await fetchHoldings(); // Refresh the list
    return data;
  };

  const deleteHolding = async (id: string) => {
    const { error } = await supabase
      .from('holdings')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    await fetchHoldings(); // Refresh the list
  };

  // Calculate summary statistics
  const totalValue = holdings.reduce((sum, holding) => sum + holding.current_value, 0);
  const totalInitialValue = holdings.reduce((sum, holding) => sum + holding.initial_value, 0);
  const totalROI = totalInitialValue > 0 ? ((totalValue - totalInitialValue) / totalInitialValue) * 100 : 0;
  const activeHoldings = holdings.filter(h => h.status === 'Active').length;

  return {
    holdings,
    loading,
    error,
    fetchHoldings,
    addHolding,
    updateHolding,
    deleteHolding,
    summary: {
      totalValue,
      totalInitialValue,
      totalROI,
      activeHoldings,
      totalHoldings: holdings.length,
    },
  };
};